/* ═══════════════════════════════════════════════════════════════════════════
   Phone numbers, by the country that issued them
   ───────────────────────────────────────────────────────────────────────────
   This product signs in two markets, and a number's shape belongs to its own
   dial code rather than to whichever country the app is configured for. That
   distinction is the whole reason this module exists:

   `format.ts#phone` took its digit count and grouping from `getCountry()`,
   which is Saudi Arabia — nine digits, `[2,3,4]`. Applied to a Pakistani number
   it kept the **last nine** of ten, so `+923479061474` rendered as
   `+966 47 906 1474`: the wrong country code, and the first digit of the number
   silently gone. On the sign-in screen that also fed a controlled input, so the
   next keystroke wrote the truncated value back into state.

   `Auth.tsx` had already reached the right conclusion and kept its own table —
   its comment says the country config "decides currency, districts and address
   shape, and none of those change because somebody's phone is on a Pakistani
   network". It just had no way to hand that table to a formatter. This is that
   table, in one place, read by every screen with a number on it.

   ── What a picker has to survive ────────────────────────────────────────────
   A country select beside an `<input type="tel">` is four lines of JSX and
   about a dozen ways to lose a customer, every one of which was live somewhere
   in this codebase:

   · **Arabic-Indic digits.** An Arabic keyboard types ٠٥٠١٢٣٤٥٦٧, a `\D` strip
     deletes all ten of them, and the submit button never enables. The Go domain
     has folded these since `NormalizeSearch`; no client did.
   · **A pasted international number.** `+966501234567` into a field that counts
     nine digits is twelve, so paste — how anybody moves a number off a contact
     card — was the one input guaranteed to be refused.
   · **The trunk zero.** `0501234567` is what a Saudi writes and `03479061474`
     is what a Pakistani writes. They are the same numbers as `+966 5…` and
     `+92 3…`, and the server has always known that.
   · **A number of the right length and the wrong kind.** `+966412345678` is
     nine digits and is a Riyadh landline. Length alone enabled the button and
     the server answered 400 — the failure that teaches nobody anything — so the
     leading digit is part of the shape.
   · **The caret.** A controlled input that regroups on every keystroke sends
     the caret to the end, which makes correcting the third digit of your own
     phone number impossible. `editPhone` is the answer, and it lives here
     rather than in three components because it is arithmetic, not markup.

   Nothing here knows about React. The per-app `PhoneField` components are the
   markup; this is the behaviour, and it is vendored byte-for-byte into the
   partner and admin bundles so the three cannot drift again.
   ═══════════════════════════════════════════════════════════════════════════ */

/** The markets this product issues sign-ins for. Two, deliberately: a third is
 *  a row in `PHONE_SHAPES` and nothing else here, but it is also districts,
 *  currency and payment rails elsewhere, so it is not a change anybody makes by
 *  accident. */
export type PhoneCountry = 'SA' | 'PK'

export interface PhoneShape {
  code: PhoneCountry
  dial: string
  /** National significant number length, excluding the dial code. */
  digits: number
  /** What the field shows when empty. Grouping is derived from it, so the hint
   *  and the formatted value cannot drift apart. */
  placeholder: string
  /** The digit every mobile number in this market begins with, once the trunk
   *  prefix is off. Nine digits starting `4` is a Riyadh landline and the server
   *  refuses it, so this is checked here rather than discovered by a 400 after
   *  the form was submitted. */
  leading: string
}

/* A non-empty tuple rather than an array: pickers default to `PHONE_SHAPES[0]`,
   and under `noUncheckedIndexedAccess` an array index is possibly-undefined.
   The first entry is the home market. */
export const PHONE_SHAPES: readonly [PhoneShape, ...PhoneShape[]] = [
  { code: 'SA', dial: '+966', digits: 9, placeholder: '5X XXX XXXX', leading: '5' },
  { code: 'PK', dial: '+92', digits: 10, placeholder: '3XX XXXXXXX', leading: '3' },
]

/** The home market — where an empty field starts. */
export const DEFAULT_SHAPE: PhoneShape = PHONE_SHAPES[0]

/* Longest dial code first, so `+966` is never claimed by `+92`. Sorted once at
   module load rather than per call: `shapeForNumber` is reached from list
   rendering, and a `toSorted` inside it allocated a new array per row. */
const BY_DIAL: readonly PhoneShape[] = [...PHONE_SHAPES].sort(
  (a, b) => b.dial.length - a.dial.length,
)

/** The shape for an ISO code, falling back to the home market.
 *
 *  Total rather than partial on purpose: every caller has a code that came out
 *  of a `<select>` whose options are these very shapes, and a
 *  `PhoneShape | undefined` there is a null check written five times to
 *  describe a state that cannot occur. */
export function shapeOf(code: string): PhoneShape {
  return PHONE_SHAPES.find((s) => s.code === code) ?? DEFAULT_SHAPE
}

/** The shape whose dial code the number starts with. */
export function shapeForNumber(e164: string): PhoneShape | undefined {
  const s = e164.trim()
  return BY_DIAL.find((shape) => s.startsWith(shape.dial))
}

/* ── digits ───────────────────────────────────────────────────────────────── */

/** Every digit in the string, as ASCII, in order.
 *
 *  Arabic-Indic (`٠`–`٩`, U+0660) and Extended Arabic-Indic (`۰`–`۹`, U+06F0)
 *  fold to `0`–`9`. This is not a nicety: the app ships an Arabic locale with an
 *  Arabic keyboard behind it, a `\D` strip deletes every one of those glyphs,
 *  and the customer watches a submit button stay grey while a correct phone
 *  number sits in the field. */
export function asciiDigits(raw: string): string {
  let out = ''
  for (const ch of raw) {
    const cp = ch.codePointAt(0) ?? 0
    if (cp >= 0x30 && cp <= 0x39) out += ch
    else if (cp >= 0x0660 && cp <= 0x0669) out += String.fromCharCode(cp - 0x0660 + 0x30)
    else if (cp >= 0x06f0 && cp <= 0x06f9) out += String.fromCharCode(cp - 0x06f0 + 0x30)
  }
  return out
}

/* ── the value a field holds ──────────────────────────────────────────────── */

/** A number mid-edit: which country, and the national digits typed so far.
 *
 *  Not an E.164 string, because a half-typed number has no E.164 form and a
 *  field that stores one has to invent something for `+9665`. Two fields that
 *  are always meaningful beat one that is meaningful only at the end. */
export interface PhoneValue {
  code: PhoneCountry
  /** National significant digits: no dial code, no trunk zero, no spaces. */
  national: string
}

export const EMPTY_PHONE: PhoneValue = { code: DEFAULT_SHAPE.code, national: '' }

/** The E.164 form the API and the identity provider both want. */
export function phoneE164(value: PhoneValue): string {
  return `${shapeOf(value.code).dial}${value.national}`
}

/** Whether this is a whole number of the kind its market issues.
 *
 *  Length **and** leading digit. Length alone is what let `+966412345678`
 *  through a form and into a 400 — see `PhoneShape.leading`. */
export function phoneComplete(value: PhoneValue): boolean {
  const shape = shapeOf(value.code)
  return value.national.length === shape.digits && value.national.startsWith(shape.leading)
}

/** Whether anything has been typed. Distinct from complete: an empty optional
 *  field is fine and a half-typed one never is. */
export function phoneEmpty(value: PhoneValue): boolean {
  return value.national === ''
}

/* ── reading a number out of whatever arrived ─────────────────────────────── */

/** Strips a country code the digits carry themselves.
 *
 *  Deliberately eager — one digit after the code is enough — because that is
 *  what makes typing `9665…` into a field already showing `+966` snap to the
 *  national form instead of dead-ending against the length cap. Eager is safe
 *  here precisely because of `leading`: a Saudi national number starts `5` and a
 *  Pakistani one starts `3`, so no national number this product accepts can
 *  begin `92` or `966` and be mistaken for one carrying its own code. */
function withoutCountryCode(digits: string): PhoneValue | undefined {
  for (const shape of BY_DIAL) {
    const cc = shape.dial.slice(1)
    if (digits.length <= cc.length || !digits.startsWith(cc)) continue
    const rest = digits.slice(cc.length).replace(/^0+/, '')
    if (rest !== '' && rest.startsWith(shape.leading)) {
      return { code: shape.code, national: rest.slice(0, shape.digits) }
    }
  }
  return undefined
}

function under(shape: PhoneShape, national: string): PhoneValue {
  return { code: shape.code, national: national.replace(/^0+/, '').slice(0, shape.digits) }
}

/** Reads a number out of anything a person can put in a field.
 *
 *  `current` is the country the picker is showing, and it decides only the cases
 *  the text itself does not: a bare national number, and a local `05…`. Text
 *  that names its own country — a leading `+`, a leading `00`, or a recognised
 *  calling code — overrides the picker, because `+92…` pasted into a field set
 *  to Saudi Arabia is somebody telling us their country, not making a mistake
 *  about it. */
export function parsePhone(raw: string, current: PhoneShape = DEFAULT_SHAPE): PhoneValue {
  const text = raw.trim()
  const digits = asciiDigits(text)
  // `00` is how both markets dial out, and it is what a number copied off a
  // printed card most often carries.
  const declared = text.startsWith('+') || digits.startsWith('00')
  const body = digits.startsWith('00') ? digits.slice(2) : digits

  if (declared) {
    for (const shape of BY_DIAL) {
      const cc = shape.dial.slice(1)
      if (body.startsWith(cc)) return under(shape, body.slice(cc.length))
    }
    /* A `+` in front of a code we do not serve. The digits are kept under the
       picked country so the field is not silently emptied; `phoneComplete` will
       refuse it, which is the honest answer — we cannot sign this number in, and
       blanking the field would read as the paste having failed. */
    return under(current, body)
  }

  /* A trunk zero names a country too, in a two-market world: `05…` is Saudi and
     `03…` is Pakistani, because neither market issues the other's leading
     digit. `domain.NormalizePhone` has folded both since it was written; the
     forms were the one thing the clients read and the picker did not, so a
     Pakistani partner typing the number off their own SIM card was told it was
     not a mobile number.

     Only where exactly one shape claims the digit. Two markets sharing a
     leading digit is a real possibility for a third entry in `PHONE_SHAPES`,
     and guessing between them would be worse than leaving the picker alone. */
  if (body.startsWith('0')) {
    const local = body.replace(/^0+/, '')
    const claims = PHONE_SHAPES.filter((s) => local.startsWith(s.leading))
    const [only] = claims
    if (local !== '' && claims.length === 1 && only) return under(only, local)
  }

  return withoutCountryCode(body) ?? under(current, body)
}

/* ── rendering ────────────────────────────────────────────────────────────── */

/** `'5X XXX XXXX'` → `[2, 3, 4]`. */
const groupsOf = (placeholder: string): number[] =>
  placeholder.split(' ').map((run) => run.length)

/** Group the national part of a number: `'501234567'` → `'50 123 4567'`.
 *
 *  Progressive: `'5012'` is `'50 12'`, so the field reads the way the
 *  placeholder above it promised from the second keystroke rather than snapping
 *  into shape at the ninth. That is only safe alongside `editPhone`, which puts
 *  the caret back where the typist left it — regrouping under a caret that jumps
 *  to the end is what the old "leave a half-typed number alone" rule was
 *  avoiding, and the caret was the part that was missing.
 *
 *  Never drops a digit. Anything past the last group joins it, so a stored
 *  number longer than its shape renders ugly rather than truncated — which is
 *  the whole lesson of the `+966 47 906 1474` bug. */
export function formatNational(raw: string, shape: PhoneShape): string {
  const d = asciiDigits(raw).replace(/^0+/, '')
  if (d === '') return ''

  const groups = groupsOf(shape.placeholder)
  const out: string[] = []
  let at = 0
  for (let i = 0; i < groups.length && at < d.length; i++) {
    const size = groups[i] ?? 0
    // The last group takes the remainder rather than its nominal width.
    out.push(i === groups.length - 1 ? d.slice(at) : d.slice(at, at + size))
    at += size
  }
  return out.join(' ')
}

/** A stored E.164 number, rendered for reading: `'+923479061474'` →
 *  `'+92 347 9061474'`.
 *
 *  Unknown dial codes come back unchanged. A number we cannot parse is still a
 *  number the customer recognises; reformatting it on a guess is not. */
export function formatE164(e164: string): string {
  const shape = shapeForNumber(e164)
  if (!shape) return e164
  return `${shape.dial} ${formatNational(e164.slice(shape.dial.length), shape)}`
}

/** Any accepted form, rendered internationally: `'0501234567'` and
 *  `'+966501234567'` both read `'+966 50 123 4567'`.
 *
 *  For lists and rows, where what arrives depends on which store answered — the
 *  API normalises to E.164 and the mocks hold the local form, and a team list
 *  that renders one of those raw is the difference between a screen that looks
 *  finished and one that does not. Anything that is not a whole number of a
 *  shape we know comes back untouched. */
export function formatAnyPhone(raw: string, fallback: PhoneShape = DEFAULT_SHAPE): string {
  const value = parsePhone(raw, fallback)
  return phoneComplete(value) ? formatE164(phoneE164(value)) : raw
}

/* ── the caret ────────────────────────────────────────────────────────────── */

/** How many digits stand before `caret` in `text`. */
export function digitsBefore(text: string, caret: number): number {
  return asciiDigits(text.slice(0, Math.max(0, caret))).length
}

/** The offset just past the `count`-th digit of `text`. */
export function caretAfterDigits(text: string, count: number): number {
  if (count <= 0) return 0
  let seen = 0
  for (let i = 0; i < text.length; i++) {
    const cp = text.charCodeAt(i)
    if (cp >= 0x30 && cp <= 0x39) {
      seen += 1
      if (seen === count) return i + 1
    }
  }
  return text.length
}

/** What a phone field should show, hold, and put its caret at after one edit. */
export interface PhoneEdit {
  value: PhoneValue
  /** The formatted text to render. */
  text: string
  /** Where to put the caret afterwards. */
  caret: number
}

/** Everything an `<input>` needs to behave, in one pure function, because the
 *  three apps have three design systems and one set of arithmetic.
 *
 *  `previous` is the formatted text the field was showing, `next` is
 *  `event.target.value`, and `caret` is `event.target.selectionStart`.
 *
 *  The deletion branch is the subtle one. With separators inserted by the
 *  formatter, backspacing over the space in `50 123 4567` deletes a character
 *  the formatter immediately puts back, so the key appears to do nothing and the
 *  *second* press finally removes a digit. Detecting it is easy — the text got
 *  shorter and the digits did not change — and the fix is to delete the digit
 *  the typist was aiming past. */
export function editPhone(
  previous: string,
  next: string,
  caret: number,
  current: PhoneShape,
): PhoneEdit {
  let seen = digitsBefore(next, caret)
  let raw = next

  if (next.length < previous.length && seen > 0 && asciiDigits(next) === asciiDigits(previous)) {
    const digits = asciiDigits(next)
    raw = digits.slice(0, seen - 1) + digits.slice(seen)
    seen -= 1
  }

  const value = parsePhone(raw, current)
  const text = formatNational(value.national, shapeOf(value.code))
  return {
    value,
    text,
    /* A paste that named its own country rewrote the whole field, so there is no
       "where they were" to return to — the end is the only honest answer. */
    caret:
      value.code === current.code
        ? caretAfterDigits(text, Math.min(seen, value.national.length))
        : text.length,
  }
}
